import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { environment } from '../../../../environments/environment';
import { IResponseData } from '../../interfaces/response-data';
import { IUnitGroup } from '../../interfaces/unit-group';
import { ISurroundings, Surroundings, SurroundingsIcon } from '../../interfaces/surroundings';
import { IProperty } from '../../interfaces/property';
import { IOffer } from '../../interfaces/offer';
import { LocationService } from '../location/location.service';
import { convertObjectToCamelCase } from '../../../utils/object/object';
import { IPropertiesApiCollectionResponse, IPropertiesApiResourceResponse } from '../../interfaces/properties-api';

export enum PropertyExpandTypes {
  Otas = 'otas',
  GuestCommunication = 'guest_communication'
}

@Injectable({
  providedIn: 'root'
})
export class PropertyService {
  openCalendarSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  observable: Observable<string | IResponseData<IUnitGroup>>;
  language: string | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly locationService: LocationService,
    public translateService: TranslateService
  ) {}

  createHeaders(channelHeader: boolean = false) {
    let headers = new HttpHeaders();
    headers = headers.set('X-Language', this.translateService.currentLang);

    if (channelHeader) {
      headers = headers.set('X-Channel-Code', 'none');
    }

    return { headers };
  }

  getListing(
    cityId?: number,
    arrivalDate?: string,
    departureDate?: string,
    rooms?: number,
    adults?: number,
    childrenAges?: string,
    expand?: PropertyExpandTypes[]
  ): Observable<any> {
    const expandParams = expand?.join(',');

    return this.http.get(
      `${environment.apiBaseUrl}/public/properties/?` +
        `${cityId ? 'cityId=' + cityId + '&' : ''}` +
        `${arrivalDate ? 'arrival=' + arrivalDate + '&' : ''}` +
        `${departureDate ? 'departure=' + departureDate + '&' : ''}` +
        `${rooms ? 'rooms=' + rooms + '&' : ''}` +
        `${adults ? 'adults=' + adults : ''}` +
        `${childrenAges ? '&childrenAges=' + childrenAges : ''}` +
        `${expand ? `&expand=${expandParams}` : ''}`,
      this.createHeaders(false)
    );
  }

  deepEqual(obj1, obj2) {
    const ok = Object.keys,
      tx = typeof obj1,
      ty = typeof obj2;
    return obj1 && obj2 && tx == 'object' && tx == ty
      ? ok(obj1).length == ok(obj2).length && ok(obj1).every((key) => this.deepEqual(obj1[key], obj2[key]))
      : obj1 == obj2;
  }

  getPropertySurroundings(propertyId: number): Observable<IResponseData<Surroundings[]>> {
    return this.http.get<IResponseData<Surroundings[]>>(
      `${environment.apiBaseUrl}/properties/${propertyId}/browse-surroundings?cat=100-1100,200-2000,200-2200,300-3000,300-3100`
    );
  }

  addIcons(surroundings: Surroundings[]): Surroundings[] {
    return surroundings.map((surrounding) => {
      const mainCategory = surrounding.categories.find((category) => category?.primary);
      // Category ID is composed by 3 levels, e.g. 100-1000-0001, where 1st level is high
      // level grouping, e.g. 100 is "Eat and Drink", 2nd level is logical sub-category,
      // e.g. 1000 is "Restaurant", 3rd level provdes more granularity, e.g 0001
      // is "Fine Dining". However we don’t need such level of granularity as it is enough
      // to display main categories like cafe, restaurant, etc. Therefore we use only 1st and
      // 2n levels to differentiate between type of the place and match it to the correct icon.
      //
      // Categories are defined in Here Maps developer guide, see:
      // https://www.here.com/docs/bundle/geocoding-and-search-api-developer-guide/page/topics-places/places-category-system-full.html
      const mainCategoryId = mainCategory?.id.slice(0, 8);
      // Default to general sightseeing icon in case there is no matching category.
      const imageName = SurroundingsIcon[mainCategoryId] ? SurroundingsIcon[mainCategoryId] : 'sightseeing';

      return {
        ...surrounding,
        icon: environment.imgixHost + `/website/svg/surroundings/${imageName}.svg`
      };
    });
  }

  getPropertyParkingFacilities(propertyId: number): Observable<IResponseData<ISurroundings[]>> {
    return this.http.get<IResponseData<ISurroundings[]>>(
      `${environment.apiBaseUrl}/properties/${propertyId}/surroundings?cat=parking-facility`
    );
  }

  getNextSunday(date) {
    let sunday = date.getDate() - (date.getDay() - 1) + 6;
    return new Date(date.setDate(sunday));
  }

  public generateStreetAddress(property: IProperty | string) {
    if (typeof property === 'string') {
      return property;
    }

    return property && property.location
      ? property.location.addressLine1.trim() +
          ', ' +
          property.location.postalCode.trim() +
          ', ' +
          property.location.city.trim()
      : '';
  }

  getProperty(
    propertyId: number | string,
    channelHeader: boolean = false,
    guestCommunication?: boolean,
    expand?: PropertyExpandTypes[]
  ): Observable<IPropertiesApiResourceResponse<IProperty>> {
    if (guestCommunication) {
      expand.push(PropertyExpandTypes.GuestCommunication);
    }

    const expandParams = expand?.join(',');

    return this.http
      .get<
        IPropertiesApiResourceResponse<IProperty>
      >(`${environment.apiBaseUrl}/public/properties/${propertyId}` + `${expand ? `?expand=${expandParams}` : ''}`, this.createHeaders(channelHeader))
      .pipe(map((response) => (response.payload ? convertObjectToCamelCase(response) : response)));
  }

  getPropertyOffers(
    propertyId: number,
    arrival: string | null,
    departure: string | null,
    rooms: number | null,
    adults: number | null,
    childrenAges: string,
    voucherCode: string | null = null,
    unitGroupIds?: number[]
  ): Observable<IPropertiesApiCollectionResponse<IOffer>> {
    if (!propertyId || isNaN(propertyId) || propertyId <= 0) {
      return of({
        success: true,
        payload: []
      });
    }

    // No offers without full booking infos
    if (!arrival || !departure || !adults) {
      return of({
        success: true,
        payload: []
      });
    }

    let paramsObj: any = {
      arrival,
      departure,
      rooms,
      adults,
      // Ensures that request won’t fail in case wrong values are passed,
      // values like `5,` or `13,` with comma sign in the end are causing issues.
      childrenAges: childrenAges?.replace(/,$/, ''),
      voucherCode,
      unitGroupIds: unitGroupIds?.toString()
    };
    return this.http
      .get<
        IPropertiesApiCollectionResponse<IOffer>
      >(environment.apiBaseUrl + '/public/properties/' + propertyId + '/offers?' + this.locationService.objToUrlParams(paramsObj), this.createHeaders())
      .pipe(
        map((response) => {
          if (response.payload) {
            response = convertObjectToCamelCase(response);
          }
          return response;
        })
      );
  }
}
